import React, { forwardRef, useImperativeHandle } from 'react';
import { buildEmailPreviewHtml } from './emailPreviewIframeUtils';
import { useEmailPreviewContainerInteractions } from './useEmailPreviewContainerInteractions';
import { useEmailPreviewEditor } from './useEmailPreviewEditor';

interface EmailPreviewIframeProps {
  templateHtml?: string;
  customHtml?: string;
  onContentChange?: (html: string) => void;
  onEnterEditMode?: () => void;
  onExitEditMode?: () => void;
  onElementPosition?: (position: { top: number; left: number }) => void;
  onHideSpeechBubble?: () => void;
}

export interface EmailPreviewIframeRef {
  getCurrentHtml: () => string;
}

const EmailPreviewIframe = forwardRef<EmailPreviewIframeRef, EmailPreviewIframeProps>(
  (
    {
      templateHtml: templateHtmlProp,
      customHtml: customHtmlProp,
      onContentChange,
      onEnterEditMode,
      onExitEditMode,
      onElementPosition,
      onHideSpeechBubble,
    },
    ref,
  ) => {
    const templateHtml = templateHtmlProp || '';
    const initialCustomHtml = customHtmlProp || '';

    const { getCurrentHtml, handleClick, isInitialized } = useEmailPreviewEditor({
      onContentChange,
      onEnterEditMode,
      onExitEditMode,
      onHideSpeechBubble,
    });

    useImperativeHandle(
      ref,
      () => ({
        getCurrentHtml,
      }),
      [getCurrentHtml],
    );

    const emailHtml = buildEmailPreviewHtml(templateHtml, initialCustomHtml);

    const { containerRef, handleContainerClick } = useEmailPreviewContainerInteractions({
      emailHtml,
      isInitialized,
      handleClick,
      onElementPosition,
      templateHtml,
      initialCustomHtml,
    });

    if (!emailHtml) {
      return null;
    }

    return (
      <div
        ref={containerRef}
        style={{
          position: 'relative',
          width: '100%',
          height: '100%',
          overflow: 'auto',
          isolation: 'isolate',
        }}
        className="email-preview-iframe"
        dangerouslySetInnerHTML={{ __html: emailHtml }}
        onClick={handleContainerClick}
      />
    );
  },
);

EmailPreviewIframe.displayName = 'EmailPreviewIframe';

export default EmailPreviewIframe;
